import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserIdleService } from 'angular-user-idle';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

const IDLE_SECONDS = 1800;
const TIMEOUT_SECONDS = 30;
const PING_SECONDS = 120;

/**
 * Wraps angular-user-idle so the countdown shown to the user and the actual
 * forced logout are both derived from the same configured timeout, instead of
 * a hand-rolled counter racing the library's own (differently configured) timer.
 */
@Injectable({ providedIn: 'root' })
export class IdleSessionService {
  private idle = inject(UserIdleService);

  /** Fires once per idle cycle, when the countdown starts (time to show the warning modal). */
  public readonly warningStarted$: Observable<void> = this.idle.onTimerStart().pipe(
    filter((count) => count === 1),
    map(() => undefined)
  );

  /** Fires when the countdown reaches TIMEOUT_SECONDS without being reset. */
  public readonly expired$: Observable<void> = this.idle.onTimeout().pipe(map(() => undefined));

  /** Seconds remaining before logout, for display in the warning modal. */
  public readonly remainingSeconds: Signal<number> = toSignal(
    this.idle.onTimerStart().pipe(map((count) => Math.max(TIMEOUT_SECONDS - (count ?? 0), 0))),
    { initialValue: TIMEOUT_SECONDS }
  );

  constructor() {
    this.idle.setConfigValues({ idle: IDLE_SECONDS, timeout: TIMEOUT_SECONDS, ping: PING_SECONDS });
  }

  start(): void {
    this.idle.startWatching();
  }

  stop(): void {
    try {
      this.idle.stopWatching();
    } catch {
      // angular-user-idle throws if startWatching was never called — safe to ignore
    }
  }

  reset(): void {
    this.idle.resetTimer();
  }
}
